import {Show, Text} from 'platform/foundation';
import styled, {css} from 'styled-components';

import {useMemo} from 'react';

import {isNil, isNotNil, join} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import {getPresetDisplayName} from '../../../../../components/Filter/components/SavedFilters/utils/getPresetDisplayName';
import type {IsKnownPresetFeature} from '../../../../../components/Filter/components/SavedFilters/utils/normalizePresetFeatures';
import {formValuesToRequestValues} from '../../../../../components/Filter/utils/formValuesToRequestValues';
import {MetricsSkeleton} from '../../../../../components/SavedFiltersPanel/components/MetricsSkeleton';
import {useMarketMetrics} from '../../../../../components/SavedFiltersPanel/hooks/useMarketMetrics';
import {
  buildFilterSummary,
  FilterSummaryHelpers,
} from '../../../../../components/SavedFiltersPanel/utils/buildFilterSummary';
import {
  resolvePresetFormValues,
  toMetricsRequestBody,
} from '../../../../../components/SavedFiltersPanel/utils/buildMetricsRequestBody';
import {formatCompactNumber} from '../../../../../components/SavedFiltersPanel/utils/formatCompactNumber';
import {getPositionFillColor} from '../../../../../components/SavedFiltersPanel/utils/getPositionFillColor';
import i18n from '../../../../../i18n/i18n';
import {GetTrimLevelsApiResponse, RelativeFilterResponseBody} from '../../../../../types/api/api';
import {FilterFormData} from '../../../../../types/FilterFormData';
import {PriceReportType} from '../../../../../types/PriceReportType';

const RATIO_DECIMALS = 1;

interface SavedFilterOverviewRowProps extends TestIdProps {
  preset: RelativeFilterResponseBody;
  priceReport: PriceReportType;
  baseFormValues: FilterFormData;
  trimLevelsCatalog: GetTrimLevelsApiResponse['trim_levels'] | null;
  isKnownFeature: IsKnownPresetFeature;
  summaryHelpers: FilterSummaryHelpers;
  formatNumber: (value: number, decimals?: number) => string | null;
  onOpen: (preset: RelativeFilterResponseBody) => void;
}

/** One compact dashboard row — market snapshot of a saved filter, click-through to Statistics. */
export function SavedFilterOverviewRow(props: SavedFilterOverviewRowProps) {
  const {preset, priceReport, baseFormValues, trimLevelsCatalog, isKnownFeature, formatNumber} =
    props;

  const plainBody = useMemo(
    () =>
      formValuesToRequestValues(
        resolvePresetFormValues(
          preset.config,
          priceReport,
          baseFormValues,
          trimLevelsCatalog,
          isKnownFeature
        )
      ),
    [preset.config, priceReport, baseFormValues, trimLevelsCatalog, isKnownFeature]
  );

  const queryArg = useMemo(
    () => ({
      reportId: priceReport.id,
      body: toMetricsRequestBody(plainBody, priceReport.sellingPrice.recommended.value ?? null),
    }),
    [priceReport, plainBody]
  );
  const {metrics, isLoading} = useMarketMetrics(queryArg, priceReport);

  const description = useMemo(
    () => join(', ', buildFilterSummary(plainBody, props.summaryHelpers)),
    [plainBody, props.summaryHelpers]
  );

  const compact = (value: number | null): string =>
    isNil(value) ? '–' : formatCompactNumber(value, formatNumber);

  const position = metrics?.position ?? null;
  const isEmptyMarket = !isLoading && metrics?.totalHits === 0;
  const demandExceedsSupply = metrics?.supplyDemand.demandExceedsSupply ?? false;
  const ratio = metrics?.supplyDemand.ratio ?? null;

  return (
    <RowButton
      type="button"
      onClick={() => props.onOpen(preset)}
      data-testid={suffixTestId(`overviewPresetRow-${preset.id}`, props)}
    >
      <NameCell>
        <Text size="small" alternative noWrap>
          {getPresetDisplayName(preset)}
        </Text>
        {isNotNil(description) && description.length > 0 ? (
          <HoverReveal>
            <Text size="xSmall" color="tertiary" noWrap>
              {description}
            </Text>
          </HoverReveal>
        ) : null}
      </NameCell>

      {isLoading ? (
        <LoadingCell>
          <MetricsSkeleton width="100%" height={4} />
        </LoadingCell>
      ) : isEmptyMarket ? (
        <EmptyCell>
          <Text size="xSmall" color="tertiary" noWrap>
            {i18n.t('entity.filterForm.savedFiltersPanel.emptyMarket')}
          </Text>
        </EmptyCell>
      ) : (
        <>
          <PositionCell>
            <GroupStartTrack>
              <PositionFill $percentage={position?.percent ?? 0} />
            </GroupStartTrack>
            <NumberSlot>
              <Text size="xSmall" alternative noWrap>
                {isNotNil(position) ? `${compact(position.place)}/${compact(position.total)}` : '–'}
              </Text>
            </NumberSlot>
            <NumberSlot>
              <Text size="xSmall" color="tertiary" noWrap>
                {isNotNil(position) ? `(${position.percent} %)` : ''}
              </Text>
            </NumberSlot>
          </PositionCell>

          <NumericCell>
            {/* The stacked mobile rows have no column headers — label inline instead */}
            <Show onMobile onTablet>
              <MetricLabel>
                <Text size="xSmall" color="secondary" noWrap>
                  {i18n.t('entity.filterForm.savedFiltersPanel.advertised')}
                </Text>
              </MetricLabel>
            </Show>
            <GroupStartSlot>
              <Text size="xSmall" alternative noWrap>
                {compact(metrics?.advertised.count ?? null)}
              </Text>
            </GroupStartSlot>
            <NumberSlot>
              {isNotNil(metrics?.advertised.averagePrice) ? (
                <Text size="xSmall" alternative noWrap>
                  {`· Ø ${compact(metrics.advertised.averagePrice)}`}
                </Text>
              ) : null}
            </NumberSlot>
          </NumericCell>

          <NumericCell>
            <Show onMobile onTablet>
              <MetricLabel>
                <Text size="xSmall" color="secondary" noWrap>
                  {i18n.t('entity.filterForm.savedFiltersPanel.sold90')}
                </Text>
              </MetricLabel>
            </Show>
            <GroupStartSlot>
              <Text
                size="xSmall"
                alternative
                noWrap
                color={demandExceedsSupply ? 'success' : 'danger'}
              >
                {compact(metrics?.sold.count ?? null)}
              </Text>
            </GroupStartSlot>
            <NumberSlot>
              {isNotNil(metrics?.sold.averagePrice) ? (
                <Text size="xSmall" alternative noWrap>
                  {`· Ø ${compact(metrics.sold.averagePrice)}`}
                </Text>
              ) : null}
            </NumberSlot>
          </NumericCell>

          <RatioCell>
            {isNotNil(ratio) ? (
              <RatioBadge $demandExceedsSupply={demandExceedsSupply}>
                {demandExceedsSupply ? '▲' : '▼'}{' '}
                {`${formatNumber(Math.round(ratio * 10) / 10, RATIO_DECIMALS) ?? ratio}×`}
              </RatioBadge>
            ) : null}
          </RatioCell>
        </>
      )}
    </RowButton>
  );
}

const RowButton = styled.button`
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: subgrid;
  align-items: center;

  border: none;
  text-align: left;
  cursor: pointer;
  color: inherit;
  background-color: transparent;
  padding: ${({theme}) => `${theme.getSize(2)} 0`};
  border-radius: ${({theme}) => theme.radii.small};

  & + & {
    border-top: 1px solid ${({theme}) => theme.colors.palettes.neutral[30][100]};
  }

  &:hover,
  &:focus-visible {
    background-color: ${({theme}) => theme.colors.palettes.blue[10][100]};
    outline: none;
  }

  /* HoverReveal is defined below — lazy interpolation avoids the TDZ */
  &:hover ${() => HoverReveal}, &:focus-visible ${() => HoverReveal} {
    max-height: ${({theme}) => theme.getSize(5)};
    opacity: 1;
  }

  @media ${({theme}) => theme.devices.mobile}, ${({theme}) => theme.devices.tablet} {
    row-gap: ${({theme}) => theme.getSize(1)};
    padding: ${({theme}) => `${theme.getSize(3)} 0`};
  }
`;

/** Filter specification stays hidden until the row is hovered — keeps rows compact. */
const HoverReveal = styled.div`
  max-height: 0;
  opacity: 0;
  overflow: hidden;
  transition:
    max-height 120ms ease,
    opacity 120ms ease;

  /* Touch layouts have no hover — the description is always visible */
  @media ${({theme}) => theme.devices.mobile}, ${({theme}) => theme.devices.tablet} {
    max-height: ${({theme}) => theme.getSize(5)};
    opacity: 1;
  }
`;

const NameCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({theme}) => theme.getSize(1)};
  min-width: 0;
`;

const LoadingCell = styled.div`
  grid-column: 2 / -1;
  margin-left: ${({theme}) => theme.getSize(8)};

  @media ${({theme}) => theme.devices.mobile}, ${({theme}) => theme.devices.tablet} {
    grid-column: 1 / -1;
    margin-left: 0;
  }
`;

const EmptyCell = styled.div`
  grid-column: 2 / -1;
  margin-left: ${({theme}) => theme.getSize(8)};
  display: flex;
  align-items: center;

  @media ${({theme}) => theme.devices.mobile}, ${({theme}) => theme.devices.tablet} {
    grid-column: 1 / -1;
    margin-left: 0;
  }
`;

/*
 * The cells are only semantic groups — display: contents lets their values flow
 * straight into the card grid, so each value shares one content-sized track with
 * the rows above and below (aligned, yet packed tightly together).
 * Stacked layout gives them a real flex box instead: one full-width row per group.
 */
const PositionCell = styled.div`
  display: contents;

  @media ${({theme}) => theme.devices.mobile}, ${({theme}) => theme.devices.tablet} {
    display: flex;
    grid-column: 1 / -1;
    align-items: center;
    gap: ${({theme}) => theme.getSize(2)};
    min-width: 0;
  }
`;

const NumericCell = styled.div`
  display: contents;

  @media ${({theme}) => theme.devices.mobile}, ${({theme}) => theme.devices.tablet} {
    display: flex;
    grid-column: 1 / -1;
    align-items: baseline;
    gap: ${({theme}) => theme.getSize(1)};
    min-width: 0;
  }
`;

/** Inline row label for the stacked layout — fixed width so the values line up. */
const MetricLabel = styled.div`
  min-width: ${({theme}) => theme.getSize(25)};
  flex-shrink: 0;
`;

/** Right-aligns a number inside its shared track. */
const NumberSlot = styled.div`
  display: flex;
  justify-content: flex-end;
  min-width: 0;
`;

/** First value of a labelled column — carries the wide gap separating the columns. */
const GroupStartSlot = styled(NumberSlot)`
  margin-left: ${({theme}) => theme.getSize(8)};

  @media ${({theme}) => theme.devices.mobile}, ${({theme}) => theme.devices.tablet} {
    margin-left: 0;
  }
`;

const PositionTrack = styled.div`
  width: ${({theme}) => theme.getSize(20)};
  flex-shrink: 0;
  height: ${({theme}) => theme.getSize(2)};
  border-radius: ${({theme}) => theme.radii.small};
  background-color: ${({theme}) => theme.colors.palettes.neutral[20][100]};
  overflow: hidden;
`;

const GroupStartTrack = styled(PositionTrack)`
  margin-left: ${({theme}) => theme.getSize(8)};

  @media ${({theme}) => theme.devices.mobile}, ${({theme}) => theme.devices.tablet} {
    margin-left: 0;
    flex: 1 1 auto;
  }
`;

const PositionFill = styled.div<{$percentage: number}>`
  height: 100%;
  border-radius: ${({theme}) => theme.radii.small};
  width: ${({$percentage}) => Math.max(0, Math.min(100, $percentage))}%;
  background-color: ${({theme, $percentage}) => getPositionFillColor(theme, $percentage)};
`;

const RatioCell = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-left: ${({theme}) => theme.getSize(8)};

  /* Stacked layout: the badge sits next to the filter name in the first row */
  @media ${({theme}) => theme.devices.mobile}, ${({theme}) => theme.devices.tablet} {
    grid-row: 1;
    grid-column: 2;
    margin-left: 0;
    align-self: start;
  }
`;

const RatioBadge = styled.span<{$demandExceedsSupply: boolean}>`
  padding: ${({theme}) => `${theme.getSize(1)} ${theme.getSize(2)}`};
  border-radius: ${({theme}) => theme.radii.small};
  font-size: ${({theme}) => theme.fontSizes.text.xxSmall};
  font-weight: 600;
  white-space: nowrap;
  ${({theme, $demandExceedsSupply}) =>
    $demandExceedsSupply
      ? css`
          color: ${theme.colors.palettes.green[80][100]};
          background-color: ${theme.colors.palettes.green[20][100]};
        `
      : css`
          color: ${theme.colors.palettes.orange[80][100]};
          background-color: ${theme.colors.palettes.orange[20][100]};
        `}
`;
